/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Action, State, fromTreeToList } from '@table-library/react-table-library/common';
import { Body, Cell, Header, HeaderCell, HeaderRow, Row, Table } from '@table-library/react-table-library/table';
import { useTheme } from '@table-library/react-table-library/theme';
import { CellTree, useTree, TreeExpandClickTypes, Tree } from '@table-library/react-table-library/tree';
import { useRowSelect, SelectTypes as TreeSelectTypes, Select, SelectClickTypes } from '@table-library/react-table-library/select';
import { Button, Input } from 'reactstrap';
import { buildFilteredSubtrees, buildList, buildTree, filterData, findMaxDepth, getExpandedIds, StringIdTableNode, TableData } from '../util/treeNavigation';
import { useDebouncedCallback } from 'use-debounce';
import { SURVEY } from '@constants/terminology';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { useGetSubsidiariesForCustomReportQuery } from '@api/custom-reports';
import { useGetSubsidiaryGroupsQuery } from '@api/subsidiary-groups';

export interface SubsidiarySelectionTableProps {
  initiativeId: string;
  handleSetSelectedIds?: (ids: string[]) => void;
  selectedIds?: string[];
  isDisabled?: boolean;
  hideCount?: boolean;
  className?: string;
}

const getSubsidiaryLevelOptions = (depth: number) => {
  return Array.from(Array(depth).keys()).map((level) => ({
    label: `Level ${level} ${level === 0 ? ' (Organization)' : ''}`,
    value: String(level),
    searchString: `Level ${level} ${level === 0 ? ' (Organization)' : ''}`,
  }));
};

export const SubsidiarySelectionTable = (props: SubsidiarySelectionTableProps) => {
  const { initiativeId, selectedIds, handleSetSelectedIds = () => { }, isDisabled = false, hideCount } = props;

  const [tableData, setTableData] = useState<TableData>({ nodes: [] });
  const [filteredData, setFilterData] = useState<TableData>({ nodes: [] });
  const [search, setSearch] = React.useState('');
  const [filterLevel, setFilterLevel] = React.useState<string[]>([]);
  const [filterGroupIds, setFilterGroupIds] = useState<string[]>([]);

  const { data: subsidiaryListRaw } = useGetSubsidiariesForCustomReportQuery(initiativeId, {
    skip: !initiativeId,
  });
  const subsidiaryList = useMemo(() => subsidiaryListRaw ?? [], [subsidiaryListRaw]);

  const { data: subsidiaryGroups = [] } = useGetSubsidiaryGroupsQuery(initiativeId, {
    skip: !initiativeId,
  });

  const validSubsidiaryIds = useMemo(() => new Set(subsidiaryList.map((s) => s._id)), [subsidiaryList]);

  const validSelectedIds = useMemo(
    () => (selectedIds ?? []).filter((id) => validSubsidiaryIds.has(id)),
    [selectedIds, validSubsidiaryIds]
  );

  const expandedIds = useMemo(() => {
    return getExpandedIds(subsidiaryList, initiativeId, selectedIds?.length ? selectedIds : [initiativeId]);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [subsidiaryList]);

  const handleSearch = useDebouncedCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    // debounce this
    setFilterData({ nodes: filterData(tableData.nodes, search) });
    const dataIds = fromTreeToList(filteredData.nodes).map((item) => item.id);
    tree.fns.onAddAll(dataIds);
  }, 500);

  const theme = useTheme({
    Table: `
      height: unset;
      max-height: 100%;
      --data-table-library_grid-template-columns: 40% repeat(2, minmax(0, 2fr)) repeat(2, minmax(0, 1fr));
    `,
  });

  useEffect(
    () => setFilterData({
      nodes: filterData(tableData.nodes, search),
    }),
    [search, tableData.nodes]
  );

  const maxDepth = useMemo(() => findMaxDepth(subsidiaryList, initiativeId), [subsidiaryList, initiativeId]);

  const subsidiaryLevelOptions = getSubsidiaryLevelOptions(maxDepth);

  const tree = useTree(filteredData,
    {
      state: { ids: expandedIds },
    },
    {
      treeIcon: {
        margin: '4px',
        iconDefault: <i className='fa ml-2 mr-3' />,
        iconRight: <i className='fa fa-caret-right text-ThemeIconSecondary mx-2' />,
        iconDown: <i className='fa fa-caret-down text-ThemeIconSecondary mx-2' />,
      },
      clickType: TreeExpandClickTypes.RowClick,
    }
  );

  const select = useRowSelect(filteredData,
    {
      state: { ids: validSelectedIds, isDisabled },
      onChange: onSelectChange,
    },
    {
      // On parent node click, when partially selected true=add_all, false=remove_all
      isPartialToAll: true,
      rowSelect: TreeSelectTypes.MultiSelect,
      clickType: SelectClickTypes.ButtonClick,
    }
  );

  function onSelectChange(action: Action, state: State) {
    handleSetSelectedIds(state.ids);
  }

  const filterInitiativeIds = useMemo(() => {
    if (!filterGroupIds.length) return new Set<string>();

    const selectedGroups = subsidiaryGroups.filter((g) => filterGroupIds.includes(g._id));
    const memberIds = selectedGroups
      .flatMap((g) => g.group.map((m) => m.initiativeId))
      .filter((id) => validSubsidiaryIds.has(id));
    return new Set(memberIds);
  }, [filterGroupIds, subsidiaryGroups, validSubsidiaryIds]);

  useEffect(() => {
    if (filterLevel.length) {
      setTableData(buildList({
        list: subsidiaryList,
        rootId: initiativeId,
        filterLevel: filterLevel.map(Number),
        filteredIds: filterInitiativeIds.size ? filterInitiativeIds : undefined,
      }));
    } else if (filterInitiativeIds.size) {
      setTableData(buildFilteredSubtrees(subsidiaryList, initiativeId, filterInitiativeIds));
    } else {
      setTableData(buildTree(subsidiaryList, initiativeId));
    }
  }, [subsidiaryList, filterLevel, filterInitiativeIds, initiativeId]);

  const checkIsToggleAll = (state: State): boolean => {
    if (!filterLevel.length && !filterInitiativeIds.size) {
      return state.ids.length < subsidiaryList.length;
    }
    const visibleIds = fromTreeToList(filteredData.nodes).map((item) => item.id);
    return visibleIds.filter((id) => state.ids.includes(id)).length < visibleIds.length;
  };

  const handleToggleTree = (tree: Tree<StringIdTableNode>) => {
    const dataIds = fromTreeToList(filteredData.nodes).map((item) => item.id);
    if (checkIsToggleAll(tree.state)) {
      tree.fns.onAddAll(dataIds);
    }
    else {
      tree.fns.onRemoveByIds(dataIds);
    }
  };

  const handleToggleSelect = (select: Select<StringIdTableNode>) => {
    // get all visible ids on the screen
    const dataIds = fromTreeToList(filteredData.nodes).map((item) => item.id);
    if (checkIsToggleAll(select.state)) {
      select.fns.onAddAll(dataIds);
      // expand the selected
      tree.fns.onAddAll(dataIds);
    }
    else {
      // only remove the visible ids on the screen
      select.fns.onRemoveByIds(dataIds);
    }
  }

  return (
    <>
      <div className='d-flex align-items-center gap-3'>
        <div className='flex-fill'>
          <Input
            placeholder='Search for subsidiary...'
            type='text'
            className='border-ThemeBorderDefault text-md'
            defaultValue={search}
            onChange={handleSearch}
          />
        </div>
        <div className='flex-fill'>
          <SelectFactory
            selectType={SelectTypes.MultipleSelect}
            placeholder={'Filter by level'}
            options={subsidiaryLevelOptions}
            values={filterLevel}
            onChange={(op) => setFilterLevel(op)}
            isMenuPortalTargetBody
          />
        </div>
        <div className='flex-fill'>
          <SelectFactory
            selectType={SelectTypes.MultipleSelect}
            placeholder='Filter by groups'
            options={subsidiaryGroups.map((g) => ({ label: g.name, value: g._id, searchString: g.name }))}
            values={filterGroupIds}
            onChange={(op) => setFilterGroupIds(op)}
            isMenuPortalTargetBody
          />
        </div>
        {hideCount ? null : <div className='ms-3'>{`${select.state.ids.length ?? 0} subsidiaries selected`}</div>}
      </div>

      <div className={`mt-2 collapsible-table border border-ThemeBgToolbar ${props.className ?? ''}`}
        style={{
          height: '400px',
        }}>
        <Table data={filteredData} tree={tree} select={select} theme={theme} layout={{ fixedHeader: true, custom: true }}>
          {(tableList: StringIdTableNode[]) => (
            <>
              <Header>
                <HeaderRow>
                  <HeaderCell>Subsidiary name</HeaderCell>
                  <HeaderCell>Level</HeaderCell>
                  <HeaderCell>{`Number of ${SURVEY.PLURAL}`}</HeaderCell>
                  <HeaderCell>
                    <Button
                      disabled={isDisabled}
                      color='link'
                      size='sm'
                      onClick={() => handleToggleTree(tree)}
                      className='p-0'
                    >
                      {checkIsToggleAll(tree.state) ? 'Expand all' : 'Collapse all'}
                    </Button>
                  </HeaderCell>
                  <HeaderCell className='header-button'>
                    <Button
                      disabled={isDisabled}
                      color='link'
                      size='sm'
                      onClick={() => handleToggleSelect(select)}
                      className='p-0'
                    >
                      {checkIsToggleAll(select.state) ? 'Select all' : 'Unselect all'}
                    </Button>
                  </HeaderCell>
                </HeaderRow>
              </Header>

              <Body>
                {tableList.map((item) => (
                  <Row disabled={isDisabled} key={item.id} item={item}>
                    <CellTree item={item}>{item.name}</CellTree>
                    <Cell>{item.level}</Cell>
                    <Cell>{item.surveyCount}</Cell>
                    <Cell />
                    <Cell>
                      {isDisabled ? (
                        <Input disabled type='checkbox' checked={select.state.ids.includes(item.id)} />
                      ) : (
                        <Input
                          type='checkbox'
                          onChange={() => select.fns.onToggleById(item.id)}
                          checked={select.state.ids.includes(item.id)}
                        />
                      )}
                    </Cell>
                  </Row>
                ))}
              </Body>
            </>
          )}
        </Table>
      </div>
    </>
  );
};
